<script lang="ts">
    // Comissão personalizada de um profissional, serviço a serviço (pos-api UserServiceComission).
    //
    // Regra: vale a comissão do cadastro do serviço; o que for preenchido aqui substitui, CAMPO A CAMPO, apenas
    // para este profissional. Campo vazio = herda do serviço (o valor herdado aparece como placeholder). Zero é
    // um valor explícito, diferente de vazio — por isso nada aqui é normalizado com `|| 0`.
    // Pacotes e insumos não aparecem: são cadastrados sempre sem comissão (a de um pacote é apurada nos serviços
    // que o compõem). Produto não tem atendimento, então os campos de atendimento ficam desabilitados.
    import { createEventDispatcher } from 'svelte'
    import { onMount } from 'svelte/internal'
    import { faAngleLeft, faSave } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { GridBadge, GridBadgeClassEnum, GridClass, GridCol, GridColTypeEnum, GridLine, GridZInput } from '../../_common/z/zgrid'
    import type { Service } from '../../../modules/posApi/services/service'
    import type { ServiceCategoryEntity } from '../../../modules/posApi/service-categories/service-category.entity'
    import type { UserServiceComission } from '../../../modules/posApi/user-service-comissions/user-service-comission'

    export let userId: string
    export let userName: string = ''

    const dispatch = createEventDispatcher()

    type ComissionField =
        | 'comissionPercentage'
        | 'comissionFixed'
        | 'comissionCosts'
        | 'comissionPercentageAttendant'
        | 'comissionFixedAttendant'
        | 'comissionCostsAttendant'

    // ordem = ordem das colunas. os três primeiros valem quando o profissional VENDE, os três últimos quando ATENDE.
    const FIELDS: { key: ComissionField; label: string; attendant: boolean }[] = [
        { key: 'comissionPercentage', label: 'Venda %', attendant: false },
        { key: 'comissionFixed', label: 'Venda fixo', attendant: false },
        { key: 'comissionCosts', label: 'Venda custos', attendant: false },
        { key: 'comissionPercentageAttendant', label: 'Atend. %', attendant: true },
        { key: 'comissionFixedAttendant', label: 'Atend. fixo', attendant: true },
        { key: 'comissionCostsAttendant', label: 'Atend. custos', attendant: true },
    ]

    type Row = {
        service: Service
        categoryName: string
        inputs: Record<ComissionField, GridZInput>
        hadOverride: boolean // já existia registro salvo — precisa ir no lote mesmo se for esvaziado (para ser removido)
    }

    let show = false
    let saving = false
    let onlyCustom = false
    let dirty = false
    let rows: Row[] = []
    let grid: GridClass = new GridClass()

    onMount(async () => {
        await load()
    })

    async function load() {
        try {
            show = true
            const [services, categories, overrides] = await Promise.all([
                window.api.pos.services.getAll(undefined, 60) as Promise<Service[]>,
                window.api.pos.serviceCategories.getAll() as Promise<ServiceCategoryEntity[]>,
                window.api.pos.userServiceComissions.getByUser(userId),
            ])
            const categoryName = new Map((categories || []).map((el) => [el.serviceCategoryId, el.name]))
            const overrideOf = new Map((overrides || []).map((el) => [el.serviceId, el]))
            rows = (services || [])
                .filter((el) => (el.type === 'service' || el.type === 'product') && el.status !== 0)
                .map((service) => buildRow(service, categoryName.get(service.serviceCategoryId) || '', overrideOf.get(service.serviceId)))
                .sort((a, b) => a.categoryName.localeCompare(b.categoryName) || a.service.name.localeCompare(b.service.name))
            dirty = false
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function buildRow(service: Service, categoryName: string, override: UserServiceComission): Row {
        const isProduct = service.type === 'product'
        const inputs = {} as Record<ComissionField, GridZInput>
        for (const field of FIELDS) {
            const disabled = field.attendant && isProduct
            inputs[field.key] = new GridZInput({
                type: 'number',
                // `??` e não `||`: zero salvo tem que voltar como zero, não como "vazio/herda"
                value: (disabled ? null : (override?.[field.key] ?? null)) as any,
                placeholder: disabled ? '' : formatInherited(service[field.key]),
                disabled,
                callback: () => (dirty = true),
            })
        }
        return { service, categoryName, inputs, hadOverride: !!override }
    }

    // valor do cadastro do serviço exibido no campo vazio — é o que vale enquanto nada for digitado
    function formatInherited(value: number) {
        return String(value || 0).replace('.', ',')
    }

    function normalize(value: any): number | null {
        if (value === '' || value === null || value === undefined) return null
        const number = Number(value)
        return Number.isNaN(number) ? null : number
    }

    function isCustom(row: Row) {
        return FIELDS.some((field) => normalize(row.inputs[field.key].value) !== null)
    }

    function loadGrid() {
        const next = new GridClass()
        next.title = 'Comissões' + (userName ? ' de ' + userName : '')
        next.subtitle =
            'Preencha só o que for diferente para este profissional. Campo vazio usa a comissão do cadastro do serviço (valor em cinza); zero também é um valor.'
        next.minWidth = '1100px'
        next.fontSize = '13px'
        next.cols.push(new GridCol({ content: 'Categoria' }))
        next.cols.push(new GridCol({ content: 'Serviço' }))
        for (const field of FIELDS) {
            next.cols.push(new GridCol({ content: field.label, type: GridColTypeEnum.zinput, size: 1 }))
        }
        next.cols.push(new GridCol({ content: '', type: GridColTypeEnum.badge }))
        for (const row of rows) {
            if (onlyCustom && !isCustom(row)) continue
            const line = new GridLine()
            line.slots.push({ content: row.categoryName || '-' })
            line.slots.push({ content: row.service.name + (row.service.type === 'product' ? ' (produto)' : '') })
            for (const field of FIELDS) {
                line.slots.push({ content: row.inputs[field.key] })
            }
            line.slots.push({
                content: row.hadOverride ? [new GridBadge({ label: 'personalizado', class: GridBadgeClassEnum.badge_purple })] : [],
            })
            next.lines.push(line)
        }
        grid = next
    }

    async function save() {
        try {
            saving = true
            show = true
            // vai no lote: tudo que tem algum valor + o que já existia (se foi esvaziado, o backend remove e o
            // serviço volta a herdar tudo). o que nunca foi personalizado e continua vazio não precisa ir.
            const items: UserServiceComission[] = rows
                .filter((row) => row.hadOverride || isCustom(row))
                .map((row) => {
                    const item = { userId, serviceId: row.service.serviceId } as UserServiceComission
                    for (const field of FIELDS) item[field.key] = normalize(row.inputs[field.key].value)
                    return item
                })
            await window.api.pos.userServiceComissions.saveByUser(userId, items)
            window.alert('Comissões salvas')
            await load()
        } catch (error) {
            window.error(error)
        } finally {
            saving = false
            show = false
        }
    }

    function goBack() {
        if (dirty && !confirm('Há alterações não salvas. Deseja sair mesmo assim?')) return
        dispatch('back')
    }
</script>

<Zact>
    <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={goBack} />
    <div class="form-check form-switch only-custom" title="Mostra apenas os serviços com algum valor preenchido para este profissional.">
        <input class="form-check-input" bind:checked={onlyCustom} on:change={loadGrid} type="checkbox" role="switch" id="onlyCustomComissions" />
        <label class="form-check-label" for="onlyCustomComissions">Somente personalizados</label>
    </div>
    <div class="spacer" />
    <Zbutton color="primary" icon={faSave} label="Salvar" loading={saving} disabled={saving} on:click={save} />
</Zact>

<div class="px-1 mt-2 text-muted hint">
    Pacotes não aparecem aqui: a comissão de um pacote é apurada nos serviços que o compõem. Produtos não têm comissão de atendimento.
</div>

<Zgrid class="h-space" {grid} />
<LoadSpinner {show} />

<style>
    .hint {
        font-size: 12px;
    }
    /* alinha o switch com os botões da barra (o form-check do Bootstrap usa float + padding) */
    .only-custom {
        display: flex;
        align-items: center;
        gap: 6px;
        padding-left: 0;
        margin: 0 0 0 8px;
        white-space: nowrap;
    }
    .only-custom :global(.form-check-input) {
        float: none;
        margin: 0;
    }
    .only-custom :global(.form-check-label) {
        font-size: 13px;
        cursor: pointer;
    }
</style>
